import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Aviso, Botao, Campo, CampoDeTexto, Modal } from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import type { LinhaSped } from '../../servicos/operacao';
import type { ColunaDaPlanilha } from '../../servicos/planilha';
import { formatarCompetencia } from '../../servicos/formato';
import { ErroDaApi } from '../ErroDaApi';

/**
 * Extrair o cálculo em xlsx e enviá-lo por e-mail — as duas ações do painel de
 * relatórios no Deca 1.
 */

/**
 * As colunas do "Extrair xlsx" do cálculo — as da tabela, com os valores como
 * número.
 *
 * As linhas são as da tela: pedir ao servidor para recalcular seria uma
 * segunda implementação da mesma conta, e é assim que a planilha passa a
 * divergir do que o operador está vendo. O servidor só formata.
 */
export function colunasDoCalculo(
  statusDaLinha: (l: LinhaSped) => string,
): ColunaDaPlanilha<LinhaSped>[] {
  return [
    { titulo: 'Mês', largura: 12, valor: (l) => formatarCompetencia(l.competencia) },
    { titulo: 'Status', largura: 24, valor: statusDaLinha },
    { titulo: 'ICMS apurado', formato: 'moeda', valor: (l) => l.totalSped },
    { titulo: '% utilização', formato: 'percentual', valor: (l) => l.percentSpedPercentual },
    { titulo: 'Valor de utilização', formato: 'moeda', valor: (l) => l.valueSped },
    { titulo: '% deságio', formato: 'percentual', valor: (l) => l.percentBeneficioPercentual },
    { titulo: 'Valor a ser pago', formato: 'moeda', valor: (l) => l.total },
    { titulo: 'Benefício econômico', formato: 'moeda', valor: (l) => l.valueBeneficio },
  ];
}

/**
 * Envio do cálculo por e-mail.
 *
 * ## O destinatário é escolhido, não resolvido
 *
 * Diferente dos e-mails do cronograma — onde o sistema decide quem recebe pela
 * regra de cargo (RN-059) —, aqui quem opera digita o endereço. É um envio
 * avulso, fora da fila, normalmente para alguém que pediu o número na hora.
 *
 * O assunto vem preenchido com o padrão do Deca 1 e é editável. `{{cliente_nome}}`
 * é substituído no servidor.
 */
export function ModalDeEnvioDoCalculo(props: {
  readonly clienteId: string;
  readonly ano: number;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const [destinatario, setDestinatario] = useState('');
  const [assunto, setAssunto] = useState(
    'Cálc. Benefício Econômico (Recorrente) — {{cliente_nome}}',
  );
  const [mensagem, setMensagem] = useState('');

  const enviar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<{ destinatarios: string[]; ensaio?: boolean }>('/icms/emails/calculo', {
        metodo: 'POST',
        corpo: {
          clienteId: props.clienteId,
          ano: props.ano,
          destinatario,
          assunto,
          ...(mensagem.trim() === '' ? {} : { mensagem: mensagem.trim() }),
        },
      }),
  });

  const fechar = (): void => {
    enviar.reset();
    setDestinatario('');
    setMensagem('');
    props.aoFechar();
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Enviar e-mail com o cálculo dos créditos"
      largura="larga"
      rodape={
        enviar.isSuccess ? (
          <Botao onClick={fechar}>Concluir</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              onClick={() => enviar.mutate()}
              disabled={destinatario.trim() === ''}
              carregando={enviar.isPending}
            >
              Enviar e-mail
            </Botao>
          </>
        )
      }
    >
      {enviar.isSuccess ? (
        enviar.data.ensaio === true ? (
          <Aviso tom="informacao" titulo="Modo ensaio — o e-mail não saiu">
            A mensagem foi montada para {enviar.data.destinatarios.join(', ')}, mas o envio está
            contido. Defina <span className="valor-fiscal text-xs">EMAIL_ENVIO_REAL=1</span> para
            liberar.
          </Aviso>
        ) : (
          <Aviso tom="positivo" titulo="E-mail enviado">
            Enviado para {enviar.data.destinatarios.join(', ')}.
          </Aviso>
        )
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            enviar.mutate();
          }}
        >
          <Campo
            rotulo="E-mail do destinatário"
            type="email"
            required
            ajuda="Envio avulso: aqui o endereço é digitado, não resolvido pelo cargo como nos e-mails do cronograma."
            value={destinatario}
            onChange={(e) => setDestinatario(e.target.value)}
          />
          <Campo
            rotulo="Assunto"
            ajuda="{{cliente_nome}} é substituído pelo nome da empresa no envio."
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
          />
          <CampoDeTexto
            rotulo="Mensagem adicional"
            rows={4}
            ajuda="Opcional. Entra acima da tabela de valores."
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
          />

          {enviar.isError && <ErroDaApi titulo="O e-mail não foi enviado" erro={enviar.error} />}

          <button type="submit" className="sr-only">
            Enviar
          </button>
        </form>
      )}
    </Modal>
  );
}
